import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Estados para el sistema de seguridad
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
  const [, setBlockedUntil] = useState(null);

  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Obtener la ruta desde donde venía el usuario (si fue redirigido por ProtectedRoute)
  const from = location.state?.from?.pathname || '/admin';

  // Constantes de seguridad
  const MAX_ATTEMPTS = 3;
  const BLOCK_TIME_MINUTES = 15; // 15 minutos de bloqueo
  const STORAGE_KEY = 'login_attempts';

  // useEffect para verificar si ya está bloqueado al cargar la página
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      const { attempts, blockedUntil: savedBlockedUntil } = JSON.parse(savedData);
      const now = new Date().getTime();
      
      if (savedBlockedUntil && now < savedBlockedUntil) {
        setIsBlocked(true);
        setFailedAttempts(attempts);
        setBlockedUntil(savedBlockedUntil);
        startBlockTimer(savedBlockedUntil);
      } else if (savedBlockedUntil && now >= savedBlockedUntil) {
        // El bloqueo ha expirado, limpiar datos
        localStorage.removeItem(STORAGE_KEY);
        setFailedAttempts(0);
        setIsBlocked(false);
      } else {
        setFailedAttempts(attempts || 0);
      }
    }
  }, []);

  // Función para iniciar el temporizador de bloqueo
  const startBlockTimer = (blockUntil) => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const timeLeft = Math.max(0, Math.ceil((blockUntil - now) / 1000 / 60));
      
      setBlockTimeRemaining(timeLeft);
      
      if (timeLeft <= 0) {
        clearInterval(timer);
        setIsBlocked(false);
        setFailedAttempts(0);
        setBlockedUntil(null);
        localStorage.removeItem(STORAGE_KEY);
        setError('');
      }
    }, 1000);
  };

  // Función para manejar fallos de login
  const handleFailedLogin = () => {
    const newAttempts = failedAttempts + 1;
    setFailedAttempts(newAttempts);
    
    if (newAttempts >= MAX_ATTEMPTS) {
      const blockUntil = new Date().getTime() + (BLOCK_TIME_MINUTES * 60 * 1000);
      setIsBlocked(true);
      setBlockedUntil(blockUntil);
      
      // Guardar en localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        attempts: newAttempts,
        blockedUntil: blockUntil
      }));
      
      startBlockTimer(blockUntil);
      setError(`Demasiados intentos fallidos. Acceso bloqueado por ${BLOCK_TIME_MINUTES} minutos.`);
    } else {
      const remainingAttempts = MAX_ATTEMPTS - newAttempts;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        attempts: newAttempts
      }));
      setError(`Credenciales incorrectas. Te quedan ${remainingAttempts} intento${remainingAttempts === 1 ? '' : 's'}.`);
    }
  };

  // Función para limpiar intentos después de un login exitoso
  // Función para limpiar el caché del navegador y datos locales
  const clearCache = async () => {
    try {
      console.log('🧹 Limpiando caché del navegador...');
      
      // 1. Limpiar localStorage (excepto configuraciones importantes)
      const importantKeys = ['firebase:authUser', 'firebase:config'];
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !importantKeys.some(importantKey => key.includes(importantKey))) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        console.log(`📦 Removido del localStorage: ${key}`);
      });
      
      // 2. Limpiar sessionStorage
      const sessionKeysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && !importantKeys.some(importantKey => key.includes(importantKey))) {
          sessionKeysToRemove.push(key);
        }
      }
      
      sessionKeysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
        console.log(`🔄 Removido del sessionStorage: ${key}`);
      });
      
      // 3. Limpiar caché de la aplicación si es posible
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log(`🗄️ Limpiando caché: ${cacheName}`);
            return caches.delete(cacheName);
          })
        );
      }
      
      // 4. Limpiar datos específicos de pacientes si existen
      const patientDataKeys = [
        'patients_data',
        'filtered_patients',
        'historia_clinica_cache',
        'recetas_cache',
        'consentimientos_cache',
        'inventario_cache',
        'last_sync_time'
      ];
      
      patientDataKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      
      console.log('✅ Caché limpiado exitosamente');
      
      // 5. Forzar recolección de basura si es posible
      if (window.gc) {
        window.gc();
      }
      
    } catch (error) {
      console.warn('⚠️ Error al limpiar caché:', error);
      // No lanzar el error para no interrumpir el login
    }
  };

  const handleSuccessfulLogin = async () => {
    // Limpiar caché antes de proceder
    await clearCache();
    
    // Limpiar datos del formulario de login
    localStorage.removeItem(STORAGE_KEY);
    setFailedAttempts(0);
    setIsBlocked(false);
    setError('');
    
    console.log('🔐 Login exitoso y caché limpiado');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verificar si está bloqueado
    if (isBlocked) {
      setError(`Acceso bloqueado. Intenta de nuevo en ${blockTimeRemaining} minuto${blockTimeRemaining === 1 ? '' : 's'}.`);
      return;
    }
    
    // Validaciones básicas
    if (!email || !password) {
      setError('Por favor completa todos los campos');
      return;
    }
    
    if (!email.includes('@')) {
      setError('Por favor ingresa un email válido');
      return;
    }
    
    setLoading(true);
    setError('');

    console.log('🔐 Intentando login con email/contraseña:', { email, password: '*'.repeat(password.length) });

    const result = await login(email, password);
    
    console.log('📊 Resultado del login:', result);
    
    if (result.success) {
      console.log('✅ Login exitoso, redirigiendo...');
      await handleSuccessfulLogin();
      navigate(from, { replace: true });
    } else {
      console.error('❌ Error en login:', result.error);
      // Mostrar error más específico
      setError(result.error || 'Error desconocido al iniciar sesión');
      handleFailedLogin();
    }
    
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    // Verificar si está bloqueado
    if (isBlocked) {
      setError(`Acceso bloqueado. Intenta de nuevo en ${blockTimeRemaining} minuto${blockTimeRemaining === 1 ? '' : 's'}.`);
      return;
    }
    
    setLoading(true);
    setError('');

    console.log('🔐 Intentando login con Google...');

    const result = await loginWithGoogle();
    
    console.log('📊 Resultado del login con Google:', result);
    
    if (result.success) {
      console.log('✅ Login con Google exitoso, redirigiendo...');
      await handleSuccessfulLogin();
      navigate(from, { replace: true });
    } else {
      console.error('❌ Error en login con Google:', result.error);
      // Mostrar error más específico
      setError(result.error || 'Error desconocido con Google');
      handleFailedLogin();
    }
    
    setLoading(false);
  };



  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(135deg, #fcdcf4 0%, #c46494 50%, #602442 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: { xs: 1, sm: 2 },
        position: 'relative'
      }}
    >
      {/* Botón de volver al inicio */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: { xs: 10, sm: 20 },
          left: { xs: 10, sm: 20 },
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#602442',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          },
          borderRadius: 2,
          px: { xs: 2, sm: 3 },
          py: { xs: 0.5, sm: 1 },
          fontWeight: 'bold',
          fontSize: { xs: '0.8rem', sm: '0.9rem' },
          zIndex: 1000
        }}
      >
        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Volver al Inicio</Box>
        <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>Inicio</Box>
      </Button>

      <Container component="main" maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={8}
          sx={{
            padding: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '2px solid #fcdcf4',
            mx: { xs: 1, sm: 0 },
            maxWidth: { xs: '100%', sm: 'none' }
          }}
        >
          <Box sx={{ mb: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 }
              }}
            >
              <img 
                src="/logo.png" 
                alt="Belkysvet Logo" 
                style={{ 
                  height: '60px', 
                  width: 'auto',
                  maxWidth: '150px',
                  marginRight: '8px'
                }} 
              />
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#602442', 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' },
                  textAlign: 'center'
                }}
              >
                belkysveterinaria
              </Typography>
            </Box>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#a55e88', 
                fontWeight: '500',
                fontSize: { xs: '1rem', sm: '1.2rem' }
              }}
            >
              Área Administrativa
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity={isBlocked ? "warning" : "error"} 
              sx={{ width: '100%', mb: 2 }}
              icon={isBlocked ? <WarningIcon fontSize="inherit" /> : undefined}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#c9aebc',
                  },
                  '&:hover fieldset': {
                    borderColor: '#c46494',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#602442',
                  },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  '&.Mui-focused': {
                    color: '#602442',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ 
                      color: '#c46494', 
                      fontSize: { xs: '1.2rem', sm: '1.5rem' } 
                    }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#c9aebc',
                  },
                  '&:hover fieldset': {
                    borderColor: '#c46494',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#602442',
                  },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  '&.Mui-focused': {
                    color: '#602442',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ 
                      color: '#c46494', 
                      fontSize: { xs: '1.2rem', sm: '1.5rem' } 
                    }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ 
                        color: '#a55e88',
                        padding: { xs: '6px', sm: '8px' }
                      }}
                      size={window.innerWidth < 600 ? "small" : "medium"}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                backgroundColor: isBlocked ? '#c9aebc' : '#c46494',
                '&:hover': {
                  backgroundColor: isBlocked ? '#c9aebc' : '#a55e88',
                },
                '&:disabled': {
                  backgroundColor: '#c9aebc',
                },
                borderRadius: 2,
                py: { xs: 1.2, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 'bold'
              }}
              disabled={loading || isBlocked}
            >
              {loading ? 'Iniciando sesión...' : 
               isBlocked ? `Bloqueado (${blockTimeRemaining} min)` : 
               'Iniciar Sesión'}
            </Button>
            
            <Divider sx={{ my: 2, color: '#c9aebc' }}>
              <Typography variant="body2" sx={{ color: '#8a6581', px: 2 }}>
                O continúa con
              </Typography>
            </Divider>
            
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleLogin}
              disabled={loading || isBlocked}
              sx={{
                mt: 1,
                mb: 2,
                borderColor: isBlocked ? '#c9aebc' : '#c46494',
                color: isBlocked ? '#c9aebc' : '#602442',
                '&:hover': {
                  borderColor: isBlocked ? '#c9aebc' : '#a55e88',
                  backgroundColor: isBlocked ? 'transparent' : 'rgba(196, 100, 148, 0.05)',
                },
                '&:disabled': {
                  borderColor: '#c9aebc',
                  color: '#c9aebc',
                },
                borderRadius: 2,
                py: { xs: 1.2, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" style={{ opacity: isBlocked ? 0.5 : 1 }}>
                <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? 'Iniciando con Google...' : 
               isBlocked ? `Bloqueado (${blockTimeRemaining} min)` :
               'Continuar con Google'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
